/*
 * This component is a card component for continuity across the app.
 *
 * @author Cargomatic Engineering
 * @usage <CargomaticCollapsibleCard />
 *
 */
import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getClassName } from '../../utils/ui/classes';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@cargomatic/ui';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { ChevronDown, ChevronRight } from 'lucide-react';

const CargomaticCollapsibleCard = forwardRef(
  (
    {
      children,
      grayFooter,
      header,
      headerParams,
      footer,
      className,
      dataTestId,
    },
    ref,
  ) => {
    // The dataTestId is a required field
    const key = dataTestId ? `${dataTestId}-card-state` : undefined;

    const [cardState, setCardState] = useLocalStorage(key);
    const [open, setOpen] = useState(cardState?.open ?? false);

    const footerClassName = getClassName({
      'bg-gray-50': grayFooter,
    });

    const handleOpenClick = () => {
      setOpen(!open);
      setCardState({ open: !open });
    };

    return (
      <Card
        data-testid={dataTestId}
        ref={ref}
        className={getClassName(className)}
      >
        {header && (
          <CardHeader>
            <div className="flex space-x-2">
              <button data-testid="button-cd4fe1b2" onClick={handleOpenClick}>
                {open ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              <CardTitle>
                <div data-testid="div-5d044744">{header(headerParams)}</div>
              </CardTitle>
            </div>
          </CardHeader>
        )}
        {open && children && (
          <CardContent>
            <div data-testid="div-1fc043d2">{children}</div>
          </CardContent>
        )}
        {footer && (
          <CardFooter>
            <div data-testid="div-0e71b504" className={footerClassName}>
              {footer()}
            </div>
          </CardFooter>
        )}
      </Card>
    );
  },
);

CargomaticCollapsibleCard.propTypes = {
  children: PropTypes.any,
  header: PropTypes.func,
  headerParams: PropTypes.object,
  footer: PropTypes.func,
  grayBody: PropTypes.bool,
  grayFooter: PropTypes.bool,
  removeVerticalPadding: PropTypes.bool,
  removeHorizontalPadding: PropTypes.bool,
  removeBorders: PropTypes.bool,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
};

CargomaticCollapsibleCard.defaultProps = {
  grayBody: false,
  grayFooter: false,
  removeHorizontalPadding: false,
  removeVerticalPadding: false,
  removeBorders: false,
};

export default CargomaticCollapsibleCard;
